/**
 * Session token storage.
 *
 * Deliberately framework-free so the API client can read the token without importing
 * React. The auth context (phase 2) is the only thing that should WRITE here.
 *
 * The backend issues an HS256 JWT valid for 60 minutes with no refresh token and no
 * revocation endpoint, so expiry is handled client-side: we read `exp` to warn before
 * the session dies rather than letting a request fail and wiping the page, which is
 * what the legacy app did on every 401.
 */

const TOKEN_KEY = 'leadgen.token';

type Listener = (token: string | null) => void;

const listeners = new Set<Listener>();
let cached: string | null | undefined;

/** localStorage throws in some privacy modes; the app must still run. */
function safeRead(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function safeWrite(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(TOKEN_KEY);
    else window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // In-memory only for this tab. Better than refusing to sign in.
  }
}

export function getToken(): string | null {
  if (cached === undefined) cached = safeRead();
  return cached;
}

export function setToken(token: string | null): void {
  cached = token;
  safeWrite(token);
  for (const listener of listeners) listener(token);
}

export function clearToken(): void {
  setToken(null);
}

/** Notifies on sign-in and sign-out. Returns an unsubscribe function. */
export function subscribeToToken(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Registers what happens when the server rejects a token we actually sent.
 *
 * The auth provider owns this. It fires only for requests that carried a token, so a
 * failed sign-in — which is also a 401 — is never mistaken for an expired session.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

export function notifyUnauthorized(): void {
  unauthorizedHandler?.();
}

interface JwtClaims {
  readonly exp?: number;
  readonly user_id?: number;
  readonly role?: string;
  readonly username?: string;
}

/**
 * Reads JWT claims without verifying the signature.
 *
 * Verification is the server's job. This is only used to show the user when their
 * session is about to end — never to make an access-control decision.
 */
export function readClaims(token: string | null = getToken()): JwtClaims | null {
  if (!token) return null;
  const segment = token.split('.')[1];
  if (!segment) return null;
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const parsed: unknown = JSON.parse(atob(padded));
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Milliseconds until the token expires; null when unknown. Negative when expired. */
export function millisUntilExpiry(token: string | null = getToken()): number | null {
  const exp = readClaims(token)?.exp;
  if (typeof exp !== 'number') return null;
  return exp * 1000 - Date.now();
}

export function isExpired(token: string | null = getToken()): boolean {
  const remaining = millisUntilExpiry(token);
  return remaining !== null && remaining <= 0;
}

/** Test seam — resets the module-level cache. */
export function __resetSessionForTests(): void {
  cached = undefined;
  listeners.clear();
  unauthorizedHandler = null;
}
